import { useRef, useState, useEffect } from 'react';
import { getBrands, getCampaigns, getJourneyGroups, getJourneys } from '../../api/axios'
import Header from '../../components/Header'
import EmailsTable from './EmailsTable'
import useAuth from '../../hooks/useAuth';

const Emails = () => {

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const idBrandRef = useRef();
  const [idBrand, setIdBrand] = useState('0');
  const [idBrands, setIdBrands] = useState([]);

  const idCampaignRef = useRef();
  const [idCampaign, setIdCampaign] = useState('0');
  const [idCampaigns, setIdCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);

  const idJourneyGroupRef = useRef();
  const [idJourneyGroup, setIdJourneyGroup] = useState('0');
  const [idJourneyGroups, setIdJourneyGroups] = useState([]);
  const [filteredJourneyGroups, setFilteredJourneyGroups] = useState([]);

  const idJourneyRef = useRef();
  const [idJourney, setIdJourney] = useState('0');
  const [idJourneys, setIdJourneys] = useState([]);
  const [filteredJourneys, setFilteredJourneys] = useState([]);

  const subjectRef = useRef();
  const [subject, setSubject] = useState('');

  const dateFromRef = useRef();
  const [dateFrom, setDateFrom] = useState('');

  const dateUntilRef = useRef();
  const [dateUntil, setDateUntil] = useState('');

  const yearRef = useRef();
  const [years, setYears] = useState([]);

  const { auth } = useAuth();

  const brandCodeById = {
    '1': 'RC',
    '2': 'EK',
  };

  const formatLabelWithBrand = (item = {}) => {
    const brandLabel = brandCodeById[String(item.brand_id)] ?? '';
    return brandLabel ? `${brandLabel} | ${item.name}` : item.name;
  };

  const filterJourneys = (journeysSource, brandId, journeyGroupId) => {
    let journeys = Array.isArray(journeysSource) ? journeysSource : [];

    if (brandId && brandId !== '0') {
      journeys = journeys.filter((journey) => String(journey.brand_id) === brandId);
    }

    if (journeyGroupId && journeyGroupId !== '0') {
      journeys = journeys.filter((journey) => String(journey.journey_group_id) === journeyGroupId);
    }

    return journeys;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let data = await getBrands({ auth });
        setIdBrands(data.results);

        data = await getCampaigns({ auth });
        setIdCampaigns(data.results);
        setFilteredCampaigns(data.results);

        data = await getJourneyGroups({ auth });
        setIdJourneyGroups(data.results);
        setFilteredJourneyGroups(data.results);

        data = await getJourneys({ auth });
        setIdJourneys(data.results);
        setFilteredJourneys(data.results);

        setError(null);
      } catch (fetchError) {
        setError(fetchError);
        setIdBrands(null);
        setIdCampaigns(null);
        setIdJourneyGroups(null);
        setFilteredJourneyGroups(null);
        setIdJourneys(null);
        setFilteredJourneys(null);
      }
      setIsLoading(false);
    };

    fetchData();

    const currentYear = new Date().getFullYear();
    const yList = [];
    for (let y = currentYear; y >= 2023; y--) {
      yList.push(y);
    }
    setYears(yList);

  }, [auth]);

  const collectCurrentFilters = () => ({
    idBrand: idBrandRef.current?.value ?? '0',
    idCampaign: idCampaignRef.current?.value ?? '0',
    idJourneyGroup: idJourneyGroupRef.current?.value ?? '0',
    idJourney: idJourneyRef.current?.value ?? '0',
    subject: subjectRef.current?.value ?? '',
    dateFrom: dateFromRef.current?.value ?? '',
    dateUntil: dateUntilRef.current?.value ?? '',
  });

  const applyFiltersToState = (filters) => {
    setIdBrand(filters.idBrand);
    setIdCampaign(filters.idCampaign);
    setIdJourneyGroup(filters.idJourneyGroup);
    setIdJourney(filters.idJourney);
    setSubject(filters.subject);
    setDateFrom(filters.dateFrom);
    setDateUntil(filters.dateUntil);
  };

  const downloadEmails = async (filters) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${process.env.REACT_APP_API_URL}v1/emails-export?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${auth?.accessToken}`,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Respuesta inválida del servidor');
    }

    return await response.blob();
  };

  const handleYearChange = (e) => {
    const selectedYear = e.target.value;

    if (selectedYear) {
      const janFirst = `${selectedYear}-01-01`;
      const decLast = `${selectedYear}-12-31`;

      if (dateFromRef.current) dateFromRef.current.value = janFirst;
      if (dateUntilRef.current) dateUntilRef.current.value = decLast;
    } else {
      if (dateFromRef.current) dateFromRef.current.value = '';
      if (dateUntilRef.current) dateUntilRef.current.value = '';
    }
  };

  const handleDateChange = () => {
    if (yearRef.current) {
      yearRef.current.value = '';
    }
  };

  const handleBrandChange = (e) => {
    const selectedBrandId = e.target.value;

    const campaignsSource = Array.isArray(idCampaigns) ? idCampaigns : [];
    const journeyGroupsSource = Array.isArray(idJourneyGroups) ? idJourneyGroups : [];
    const journeysSource = Array.isArray(idJourneys) ? idJourneys : [];

    if (selectedBrandId === '0') {
      setFilteredCampaigns(campaignsSource);
      setFilteredJourneyGroups(journeyGroupsSource);
      setFilteredJourneys(filterJourneys(journeysSource, selectedBrandId, '0'));
    } else {
      const campaignsForBrand = campaignsSource.filter(
        (campaign) => String(campaign.brand_id) === selectedBrandId
      );
      setFilteredCampaigns(campaignsForBrand);

      const journeyGroupsForBrand = journeyGroupsSource.filter(
        (group) => String(group.brand_id) === selectedBrandId
      );
      setFilteredJourneyGroups(journeyGroupsForBrand);

      setFilteredJourneys(filterJourneys(journeysSource, selectedBrandId, '0'));
    }

    if (idCampaignRef.current) {
      idCampaignRef.current.value = '0';
    }
    if (idJourneyGroupRef.current) {
      idJourneyGroupRef.current.value = '0';
    }
    if (idJourneyRef.current) {
      idJourneyRef.current.value = '0';
    }
  };

  const handleJourneyGroupChange = (event) => {
    const selectedGroupId = event.target.value;
    const journeysSource = Array.isArray(idJourneys) ? idJourneys : [];
    const currentBrandId = idBrandRef.current ? idBrandRef.current.value : '0';

    setFilteredJourneys(filterJourneys(journeysSource, currentBrandId, selectedGroupId));

    if (idJourneyRef.current) {
      idJourneyRef.current.value = '0';
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const filters = collectCurrentFilters();
    applyFiltersToState(filters);
  };

  const handleDownload = async () => {
    try {
      const filters = collectCurrentFilters();
      applyFiltersToState(filters);
      const blob = await downloadEmails(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `emails-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error al descargar:', downloadError);
      alert('Error al descargar el archivo. Por favor intente nuevamente.');
    }
  };

  return (
    <>
      <Header />

      <div className="content">
        <div className="emails-overview">
          <div className="table_div">
            <div className="filters_div">
              <form id="emails-filters-form" action="" onSubmit={handleSubmit}>
                <ul className="filters_row">
                  <li>
                    <label htmlFor="idBrand">
                      <span>Marca</span>
                      <select ref={idBrandRef} id="idBrand" name="idBrand" onChange={handleBrandChange}>
                        <option value="0">Todas</option>
                        {!isLoading && idBrands && idBrands.map((option, index) => (
                          <option key={index} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </li>

                  <li>
                    <label htmlFor="idCampaign">
                      <span>Campaña</span>
                      <select ref={idCampaignRef} id="idCampaign" name="idCampaign">
                        <option value="0">Todas</option>
                        {!isLoading && filteredCampaigns && filteredCampaigns.map((option, index) => {
                          const brandLabel = brandCodeById[String(option.brand_id)] ?? '';
                          const label = brandLabel ? `${brandLabel} | ${option.name}` : option.name;
                          return (
                            <option key={index} value={option.id}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </li>

                  <li>
                    <label htmlFor="idJourneyGroup">
                      <span>Journey Group</span>
                      <select
                        ref={idJourneyGroupRef}
                        id="idJourneyGroup"
                        name="idJourneyGroup"
                        onChange={handleJourneyGroupChange}
                      >
                        <option value="0">Todos</option>
                        {!isLoading && filteredJourneyGroups && filteredJourneyGroups.map((option, index) => (
                          <option key={index} value={option.id}>
                            {formatLabelWithBrand(option)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </li>

                  <li>
                    <label htmlFor="idJourney">
                      <span>Journey</span>
                      <select ref={idJourneyRef} id="idJourney" name="idJourney">
                        <option value="0">Todos</option>
                        {!isLoading && filteredJourneys && filteredJourneys.map((option, index) => (
                          <option key={index} value={option.id}>
                            {formatLabelWithBrand(option)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </li>
                </ul>

                <ul className="filters_row">
                  <li>
                    <label htmlFor="dateFrom">
                      <span>Fecha Desde</span>
                      <input
                        ref={dateFromRef}
                        name="dateFrom"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        className="w--130 center"
                        onChange={handleDateChange}
                      />
                    </label>
                  </li>

                  <li>
                    <label htmlFor="dateUntil">
                      <span>Fecha Hasta</span>
                      <input
                        ref={dateUntilRef}
                        name="dateUntil"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        className="w--130 center"
                        onChange={handleDateChange}
                      />
                    </label>
                  </li>

                  <li>
                    <label htmlFor="year">
                      <span>Año</span>
                      <select
                        ref={yearRef}
                        id="year"
                        name="year"
                        className="w--80 center"
                        onChange={handleYearChange}
                      >
                        <option value="">Todos</option>
                        {years.map((year, index) => (
                          <option key={index} value={year}>{year}</option>
                        ))}
                      </select>
                    </label>
                  </li>

                  <li>
                    <label htmlFor="subject">
                      <span>Asunto</span>
                      <input ref={subjectRef} name="subject" type="text" placeholder="Buscar por asunto" />
                    </label>
                  </li>

                  <li>
                    <button className="button-outline" type="submit">Buscar</button>
                  </li>
                  <li>
                    <button className="button-outline" type="button" onClick={handleDownload}>Descargar Excel</button>
                  </li>
                </ul>
              </form>
            </div>
            {error ? <div className="emails-inline-error">No se pudieron cargar los filtros.</div> : null}
          </div>

          <EmailsTable params={{ idBrand, idCampaign, idJourneyGroup, idJourney, subject, dateFrom, dateUntil }} />
        </div>
      </div>
    </>
  );
};

export default Emails;
